import type {FabricObjectProps, ObjectEvents, TClassProperties, TOptions} from '@postermywall/fabricjs-2';
import {classRegistry, FabricObject} from '@postermywall/fabricjs-2';
import type {SerializedObjectProps} from '@postermywall/fabricjs-2/src/shapes/Object/types';
import {LINE_CAP_STYLE_TO_OFFSET_SCALE_HASHMAP, LineDashType, LineCapStyle} from './line-item.types';
import {
  LINE_END_POINT_SIZE,
  DEFAULT_LINE_HEIGHT,
} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-cap-style-option/line-cap-style-option.types';

export const pmwFabricLineDefaultValues: Partial<TClassProperties<PMWFabricLine>> = {
  lineDashType: LineDashType.NONE,
  lineCapStart: LineCapStyle.NONE,
  lineCapEnd: LineCapStyle.NONE,
  customGapSize: 10,
  customDashSize: 30,
  isRounded: false,
};

interface UniquePMWFabricLineProps {
  lineCapEnd: LineCapStyle;
  lineCapStart: LineCapStyle;
  lineDashType: LineDashType;
  customGapSize: number;
  customDashSize: number;
  isRounded: boolean;
}

const PMW_LINE_PROPS = ['lineDashType', 'lineCapEnd', 'lineCapStart', 'isRounded', 'customGapSize', 'customDashSize'];

export interface SerializedPMWFabricLineProps extends SerializedObjectProps, UniquePMWFabricLineProps {}
export interface PMWFabricLineProps extends FabricObjectProps, UniquePMWFabricLineProps {}

export class PMWFabricLine<
    Props extends TOptions<PMWFabricLineProps> = Partial<PMWFabricLineProps>,
    SProps extends SerializedPMWFabricLineProps = SerializedPMWFabricLineProps,
    EventSpec extends ObjectEvents = ObjectEvents,
  >
  extends FabricObject<Props, SProps, EventSpec>
  implements PMWFabricLineProps
{
  declare lineDashType: LineDashType;
  declare lineCapEnd: LineCapStyle;
  declare lineCapStart: LineCapStyle;
  declare customDashSize: number;
  declare customGapSize: number;
  declare isRounded: boolean;

  static override type = 'PMWFabricLine';

  static override ownDefaults = pmwFabricLineDefaultValues;

  static override cacheProperties = [...FabricObject.cacheProperties, ...PMW_LINE_PROPS];

  static override stateProperties = [...FabricObject.stateProperties, ...PMW_LINE_PROPS];
  static override getDefaults(): Record<string, any> {
    return {
      ...super.getDefaults(),
      ...PMWFabricLine.ownDefaults,
    };
  }

  /**
   * Constructor
   */
  constructor(options: Props) {
    super(options);
    Object.assign(this, PMWFabricLine.ownDefaults);
    this.setOptions(options);
  }

  private getLineCapStartX(w: number, lineWidth: number): number {
    let lineCapStartX;
    const x = -w / 2;
    const size = LINE_END_POINT_SIZE * this.scaleY;

    if (this.lineCapStart !== LineCapStyle.NONE) {
      const capStyleOffsetScale = LINE_CAP_STYLE_TO_OFFSET_SCALE_HASHMAP[this.lineCapStart];
      const capStyleOffset = size * capStyleOffsetScale.offset;
      const scaledPointStyleOffset = this.scaleX === 0 ? 0 : capStyleOffset / this.scaleX;
      lineCapStartX = x + scaledPointStyleOffset;
    } else if (this.isRounded) {
      const capVisualRadius = lineWidth / 2;
      const scaledCapVisualRadius = this.scaleX === 0 ? 0 : capVisualRadius / this.scaleX;
      lineCapStartX = x + scaledCapVisualRadius;
    } else {
      lineCapStartX = x;
    }

    return lineCapStartX;
  }
  private getLineCapEndX(w: number, lineWidth: number): number {
    let lineCapEndX;
    const x = w / 2;
    const size = LINE_END_POINT_SIZE * this.scaleY;

    if (this.lineCapEnd !== LineCapStyle.NONE) {
      const capStyleOffsetScale = LINE_CAP_STYLE_TO_OFFSET_SCALE_HASHMAP[this.lineCapEnd];
      const capStyleOffset = size * capStyleOffsetScale.offset;
      const scaledPointStyleOffset = this.scaleX === 0 ? 0 : capStyleOffset / this.scaleX;
      lineCapEndX = x - scaledPointStyleOffset;
    } else if (this.isRounded) {
      const capVisualRadius = lineWidth / 2;
      const scaledCapVisualRadius = this.scaleX === 0 ? 0 : capVisualRadius / this.scaleX;
      lineCapEndX = x - scaledCapVisualRadius;
    } else {
      lineCapEndX = x;
    }

    return lineCapEndX;
  }

  override _render(ctx: CanvasRenderingContext2D): void {
    const {width: w} = this;
    const y = 0;
    const lineWidth = DEFAULT_LINE_HEIGHT * this.scaleY;
    const lineCapStartX = this.getLineCapStartX(w, lineWidth);
    const lineCapEndX = this.getLineCapEndX(w, lineWidth);

    ctx.strokeStyle = this.fill as string;
    ctx.fillStyle = this.fill as string;

    ctx.save();
    ctx.lineCap = this.isRounded ? 'round' : 'butt';
    ctx.lineWidth = lineWidth;
    this.setLineDashType(ctx, lineWidth);
    ctx.beginPath();
    ctx.moveTo(lineCapStartX, y);
    ctx.lineTo(lineCapEndX, y);
    if (this.strokeUniform) {
      const scaling = this.getObjectScaling();
      ctx.scale(1 / scaling.x, 1 / scaling.y);
    }
    ctx.stroke();
    ctx.restore();

    this.drawLineCapStart(ctx, lineCapStartX * this.scaleX, y, lineWidth);
    this.drawLineCapEnd(ctx, lineCapEndX * this.scaleX, y, lineWidth);
  }

  private drawLineCapStart(ctx: CanvasRenderingContext2D, x: number, y: number, lineWidth: number): void {
    if (this.lineCapStart === LineCapStyle.NONE) {
      return;
    }

    ctx.save();
    if (this.strokeUniform) {
      const scaling = this.getObjectScaling();
      ctx.scale(1 / scaling.x, 1 / scaling.y);
    }
    const size = LINE_END_POINT_SIZE * this.scaleY;
    const halfSize = size / 2;

    const isFilled = [LineCapStyle.FILLED_ARROW, LineCapStyle.FILLED_CIRCLE, LineCapStyle.FILLED_SQUARE, LineCapStyle.FILLED_DIAMOND].includes(this.lineCapStart);

    ctx.beginPath();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    switch (this.lineCapStart) {
      case LineCapStyle.T_BAR: {
        ctx.moveTo(x, y - size);
        ctx.lineTo(x, y + size);
        break;
      }
      case LineCapStyle.ARROW:
        ctx.moveTo(x + size, y - size * 0.75);
        ctx.lineTo(x - halfSize / 2, y);
        ctx.lineTo(x + size, y + size * 0.75);
        break;
      case LineCapStyle.FILLED_ARROW:
        ctx.moveTo(x - halfSize, y);
        ctx.lineTo(x + size * 0.75, y - size * 0.75);
        ctx.lineTo(x + size * 0.75, y + size * 0.75);
        break;
      case LineCapStyle.CIRCLE:
        ctx.arc(x - size * 0.75, y, size * 0.75, 0, Math.PI * 2);
        break;
      case LineCapStyle.FILLED_CIRCLE:
        ctx.arc(x - size * 0.75, y, size * 0.75, 0, Math.PI * 2);
        break;
      case LineCapStyle.SQUARE:
        ctx.rect(x - size * 1.5, y - size * 0.75, size * 1.5, size * 1.5);
        break;
      case LineCapStyle.FILLED_SQUARE:
        ctx.rect(x - size * 1.5, y - size * 0.75, size * 1.5, size * 1.5);
        break;
      case LineCapStyle.DIAMOND:
        ctx.moveTo(x - size, y - size);
        ctx.lineTo(x, y);
        ctx.lineTo(x - size, y + size);
        ctx.lineTo(x - size * 2, y);
        break;
      case LineCapStyle.FILLED_DIAMOND:
        ctx.moveTo(x - size, y - size);
        ctx.lineTo(x, y);
        ctx.lineTo(x - size, y + size);
        ctx.lineTo(x - size * 2, y);
        break;

      default:
        throw new Error(`Unknown LineDashType: ${this.lineCapStart}`);
    }

    if (this.lineCapStart !== LineCapStyle.ARROW && this.lineCapStart !== LineCapStyle.T_BAR) {
      ctx.closePath();
    }

    if (isFilled) {
      ctx.fill();
    }

    ctx.lineWidth = lineWidth;
    ctx.stroke();

    ctx.restore();
  }
  private drawLineCapEnd(ctx: CanvasRenderingContext2D, x: number, y: number, lineWidth: number): void {
    if (this.lineCapEnd === LineCapStyle.NONE) {
      return;
    }

    ctx.save();
    if (this.strokeUniform) {
      const scaling = this.getObjectScaling();
      ctx.scale(1 / scaling.x, 1 / scaling.y);
    }
    const size = LINE_END_POINT_SIZE * this.scaleY;
    const halfSize = size / 2;

    const isFilled = [LineCapStyle.FILLED_ARROW, LineCapStyle.FILLED_CIRCLE, LineCapStyle.FILLED_SQUARE, LineCapStyle.FILLED_DIAMOND].includes(this.lineCapEnd);

    ctx.beginPath();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    switch (this.lineCapEnd) {
      case LineCapStyle.T_BAR:
        ctx.moveTo(x, y - size);
        ctx.lineTo(x, y + size);
        break;

      case LineCapStyle.ARROW:
        ctx.moveTo(x - size, y - size * 0.75);
        ctx.lineTo(x + halfSize / 2, y);
        ctx.lineTo(x - size, y + size * 0.75);
        break;
      case LineCapStyle.FILLED_ARROW:
        ctx.moveTo(x + halfSize, y);
        ctx.lineTo(x - size * 0.75, y - size * 0.75);
        ctx.lineTo(x - size * 0.75, y + size * 0.75);
        break;
      case LineCapStyle.CIRCLE:
        ctx.arc(x + size * 0.75, y, size * 0.75, 0, Math.PI * 2);
        break;
      case LineCapStyle.FILLED_CIRCLE:
        ctx.arc(x + size * 0.75, y, size * 0.75, 0, Math.PI * 2);
        break;
      case LineCapStyle.SQUARE:
        ctx.rect(x, y - size * 0.75, size * 1.5, size * 1.5);
        break;
      case LineCapStyle.FILLED_SQUARE:
        ctx.rect(x, y - size * 0.75, size * 1.5, size * 1.5);
        break;
      case LineCapStyle.DIAMOND:
        ctx.moveTo(x + size, y - size);
        ctx.lineTo(x, y);
        ctx.lineTo(x + size, y + size);
        ctx.lineTo(x + size * 2, y);
        break;
      case LineCapStyle.FILLED_DIAMOND:
        ctx.moveTo(x + size, y - size);
        ctx.lineTo(x, y);
        ctx.lineTo(x + size, y + size);
        ctx.lineTo(x + size * 2, y);
        break;
      default:
        throw new Error(`Unknown lineCapEnd style: ${this.lineCapEnd}`);
    }

    if (this.lineCapEnd !== LineCapStyle.ARROW && this.lineCapEnd !== LineCapStyle.T_BAR) {
      ctx.closePath();
    }

    if (isFilled) {
      ctx.fill();
    }

    ctx.lineWidth = lineWidth;
    ctx.stroke();

    ctx.restore();
  }

  private setLineDashType(ctx: CanvasRenderingContext2D, lineWidth: number): void {
    switch (this.lineDashType) {
      case LineDashType.DASH:
        ctx.setLineDash([30, 10 * this.scaleY]);
        break;
      case LineDashType.LONG_DASH:
        ctx.setLineDash([60, 10 * this.scaleY]);
        break;
      case LineDashType.DOTED:
        ctx.setLineDash([this.isRounded ? 0 : lineWidth, 10 * this.scaleY]);
        break;
      case LineDashType.DOT_DASH:
        ctx.setLineDash([30, 10 * this.scaleY, this.isRounded ? 0 : lineWidth, 10 * this.scaleY]);
        break;
      case LineDashType.CUSTOM:
        ctx.setLineDash([this.customDashSize, this.customGapSize]);
        break;
      case LineDashType.NONE:
        ctx.setLineDash([]);
        break;
      default:
        throw new Error(`Unknown LineDashType: ${this.lineDashType}`);
    }
  }
}

classRegistry.setClass(PMWFabricLine);
